(function () {
  'use strict';

  // NOTE: These are FALLBACK plan IDs only. Real plan IDs are fetched
  // per-variant from /products/{handle}.js because each product's selling
  // plan group has its own unique IDs.
  const SELLING_PLANS = [
    { id: '4613341403', name: 'Delivery every 1 week' },
    { id: '4613374171', name: 'Delivery every 2 weeks' },
    { id: '4613406939', name: 'Delivery every 3 weeks' },
    { id: '4613439707', name: 'Delivery every 4 weeks' },
    { id: '4613505243', name: 'Delivery every 6 weeks' },
    { id: '6374818011', name: 'Delivery every 2 months' },
  ];

  const DEFAULT_SELLING_PLAN_ID = '4613341403';
  const PLAN_CACHE_TTL_MS = 30 * 60 * 1000;
  // Version the cache key so previous versions' wrong data gets ignored.
  // Bump this if plan-ID extraction logic changes meaningfully.
  const PLAN_CACHE_VERSION = 'v2';

  let cartCache = null;
  let injectScheduled = false;
  let isInjectingCustomDrawer = false;

  // Per-select pending state. Tracks whether THIS select element is
  // currently mid-API-call. No global lock, no queue, no activeUpdateCount.
  // Each select handles its own lifecycle.
  const selectsInFlight = new WeakSet();

  const productPlanCache = {};
  const ineligibleVariants = new Set();

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      const data = await res.json();
      if (data && Array.isArray(data.items)) {
        cartCache = data;
        window._fcdCart = data;
      }
      return cartCache;
    } catch (err) {
      console.warn('[CustomSub] Failed to fetch cart:', err);
      return null;
    }
  }

  // Cache: cacheKey → { hasPlans: boolean, plans: [{id, name}] }
  // The plans array contains the ACTUAL plan IDs/names for this specific
  // variant from Shopify. We use these instead of the hardcoded SELLING_PLANS
  // because plan IDs differ per product/selling-plan-group.
  async function getVariantSellingPlans(handle, variantId) {
    if (!handle) return { hasPlans: false, plans: [] };
    if (ineligibleVariants.has(String(variantId))) return { hasPlans: false, plans: [] };
    const cacheKey = `${handle}_${variantId}`;
    if (productPlanCache[cacheKey] !== undefined) return productPlanCache[cacheKey];

    try {
      const stored = sessionStorage.getItem('csub_' + PLAN_CACHE_VERSION + '_' + cacheKey);
      if (stored !== null) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object' &&
              typeof parsed.t === 'number' &&
              (Date.now() - parsed.t) < PLAN_CACHE_TTL_MS &&
              Array.isArray(parsed.plans)) {
            productPlanCache[cacheKey] = { hasPlans: !!parsed.v, plans: parsed.plans };
            return productPlanCache[cacheKey];
          }
        } catch (_) {}
      }
    } catch (e) {}

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) {
        productPlanCache[cacheKey] = { hasPlans: false, plans: [] };
        return productPlanCache[cacheKey];
      }
      const product = await res.json();
      if (!product.selling_plan_groups || !product.selling_plan_groups.length) {
        productPlanCache[cacheKey] = { hasPlans: false, plans: [] };
        try { sessionStorage.setItem('csub_' + PLAN_CACHE_VERSION + '_' + cacheKey, JSON.stringify({ v: false, plans: [], t: Date.now() })); } catch (e) {}
        return productPlanCache[cacheKey];
      }
      const variant = product.variants && product.variants.find(v => String(v.id) === String(variantId));
      let plans = [];
      if (variant && Array.isArray(variant.selling_plan_allocations) && variant.selling_plan_allocations.length > 0) {
        plans = variant.selling_plan_allocations
          .map(alloc => alloc.selling_plan)
          .filter(p => p && p.id != null && p.name)
          .map(p => ({ id: String(p.id), name: p.name }));
      }
      // Fallback: read plans from selling_plan_groups if variant didn't expose them.
      // Some products only have plans on the product-level groups.
      if (plans.length === 0 && Array.isArray(product.selling_plan_groups)) {
        const seen = new Set();
        product.selling_plan_groups.forEach(group => {
          if (Array.isArray(group.selling_plans)) {
            group.selling_plans.forEach(p => {
              if (p && p.id != null && p.name && !seen.has(String(p.id))) {
                seen.add(String(p.id));
                plans.push({ id: String(p.id), name: p.name });
              }
            });
          }
        });
      }
      const hasPlans = plans.length > 0;
      productPlanCache[cacheKey] = { hasPlans, plans };
      try { sessionStorage.setItem('csub_' + PLAN_CACHE_VERSION + '_' + cacheKey, JSON.stringify({ v: hasPlans, plans, t: Date.now() })); } catch (e) {}
      return productPlanCache[cacheKey];
    } catch (err) {
      console.warn('[CustomSub] Failed to fetch variant plans:', handle, err);
      productPlanCache[cacheKey] = { hasPlans: false, plans: [] };
      return productPlanCache[cacheKey];
    }
  }

  // Compatibility wrapper for old callers
  async function variantHasSellingPlans(handle, variantId) {
    const result = await getVariantSellingPlans(handle, variantId);
    return result.hasPlans;
  }

  // Get plans synchronously from cache (returns SELLING_PLANS fallback if not cached).
  // Used by buildSelector which is synchronous.
  function getCachedPlansOrFallback(handle, variantId) {
    if (!handle || !variantId) return SELLING_PLANS;
    const cacheKey = `${handle}_${variantId}`;
    const cached = productPlanCache[cacheKey];
    if (cached && cached.plans && cached.plans.length > 0) {
      return cached.plans;
    }
    // Try sessionStorage
    try {
      const stored = sessionStorage.getItem('csub_' + PLAN_CACHE_VERSION + '_' + cacheKey);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.plans) && parsed.plans.length > 0 &&
            typeof parsed.t === 'number' && (Date.now() - parsed.t) < PLAN_CACHE_TTL_MS) {
          productPlanCache[cacheKey] = { hasPlans: true, plans: parsed.plans };
          return parsed.plans;
        }
      }
    } catch (e) {}
    return SELLING_PLANS;
  }

  function ensureSubscriptionBadge(itemEl, planName) {
    if (!itemEl || !planName) return;
    const infoEl = itemEl.querySelector('.fcd-item__info');
    if (!infoEl) return;

    let badge = itemEl.querySelector('.fcd-item__subscription');
    const newText = '♻ ' + planName;

    if (badge) {
      if (badge.textContent !== newText) badge.textContent = newText;
      return;
    }

    badge = document.createElement('p');
    badge.className = 'fcd-item__subscription';
    badge.textContent = newText;

    const variantEl = infoEl.querySelector('.fcd-item__variant');
    const titleEl   = infoEl.querySelector('.fcd-item__title');
    const priceRow  = infoEl.querySelector('.fcd-item__price-row');

    if (variantEl && variantEl.parentNode === infoEl) {
      variantEl.insertAdjacentElement('afterend', badge);
    } else if (titleEl && titleEl.parentNode === infoEl) {
      titleEl.insertAdjacentElement('afterend', badge);
    } else if (priceRow && priceRow.parentNode === infoEl) {
      infoEl.insertBefore(badge, priceRow);
    } else {
      infoEl.appendChild(badge);
    }
  }

  function removeSubscriptionBadge(itemEl) {
    if (!itemEl) return;
    const badge = itemEl.querySelector('.fcd-item__subscription');
    if (badge) badge.remove();
  }

  // ============================================================
  // CHANGE PLAN — radically simplified version
  // ============================================================
  // Direct flow:
  //   1. User picks new plan from dropdown
  //   2. Mark the select as in-flight (visual: dim/disable)
  //   3. POST /cart/change.js with selling_plan
  //   4. On success: refetch cart, find updated line, patch DOM
  //   5. Trigger protection sync
  //   6. Mark select as no longer in-flight
  //
  // No global lock, no queue, no activeUpdateCount, no orphan cleanup
  // deferred render, no fcdRenderCart calls. Each select element is
  // independent. If user clicks rapidly, the lock prevents overlapping
  // API calls on THIS select but doesn't block others.
  // ============================================================
  async function changePlan(wrapperEl, sellingPlanId) {
    if (!wrapperEl) return;
    const selectEl = wrapperEl.querySelector('.csub-select');
    if (!selectEl) return;

    // If this select is already mid-API-call, ignore.
    if (selectsInFlight.has(selectEl)) return;
    selectsInFlight.add(selectEl);

    const lineKey = wrapperEl.getAttribute('data-line-key');
    if (!lineKey) {
      selectsInFlight.delete(selectEl);
      return;
    }

    selectEl.disabled = true;
    selectEl.classList.add('csub-select--loading');

    try {
      const preKeys = (cartCache && Array.isArray(cartCache.items))
        ? cartCache.items.map(i => i.key)
        : [];

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          selling_plan: sellingPlanId,
          properties: { _selling_plan_id: sellingPlanId },
        }),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.warn('[CustomSub] Plan change failed:', res.status, errBody);
        await fetchCart();
        // Revert select to actual cart state
        const item = cartCache?.items?.find(i => i.key === lineKey);
        if (item?.selling_plan_allocation) {
          selectEl.value = String(item.selling_plan_allocation.selling_plan.id);
        }
        return;
      }

      // Refetch authoritative cart
      const fresh = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      if (!fresh || !Array.isArray(fresh.items)) return;
      cartCache = fresh;
      window._fcdCart = fresh;

      // Find the updated line
      const itemEl = wrapperEl.closest('.fcd-item');
      const variantId = itemEl?.dataset?.variantId;

      // Match by: same key OR new key (not in preKeys) with matching plan + variant
      let updatedItem = fresh.items.find(i => i.key === lineKey);
      if (updatedItem &&
          (!updatedItem.selling_plan_allocation ||
           String(updatedItem.selling_plan_allocation.selling_plan.id) !== String(sellingPlanId))) {
        updatedItem = null;
      }
      if (!updatedItem) {
        updatedItem = fresh.items.find(i =>
          preKeys.indexOf(i.key) === -1 &&
          i.selling_plan_allocation &&
          String(i.selling_plan_allocation.selling_plan.id) === String(sellingPlanId) &&
          (variantId ? String(i.variant_id) === String(variantId) : true)
        );
      }
      if (!updatedItem) {
        updatedItem = fresh.items.find(i =>
          i.selling_plan_allocation &&
          String(i.selling_plan_allocation.selling_plan.id) === String(sellingPlanId) &&
          (variantId ? String(i.variant_id) === String(variantId) : true)
        );
      }

      if (updatedItem && wrapperEl.isConnected) {
        // Patch wrapper and item with the new key (in case Shopify rotated it)
        wrapperEl.setAttribute('data-line-key', updatedItem.key);
        if (itemEl) {
          itemEl.dataset.key = updatedItem.key;
          itemEl.querySelectorAll('.fcd-qty-btn').forEach(btn => {
            btn.dataset.key = updatedItem.key;
          });
          itemEl.querySelectorAll('.fcd-item__remove').forEach(btn => {
            btn.dataset.key = updatedItem.key;
          });

          // Update badge
          const planName = SELLING_PLANS.find(p => p.id === String(sellingPlanId))?.name || '';
          ensureSubscriptionBadge(itemEl, planName);

          // Update line price
          const priceEl = itemEl.querySelector('.fcd-item__price');
          if (priceEl) {
            const newPrice = formatMoney(updatedItem.final_line_price);
            if (priceEl.textContent !== newPrice) priceEl.textContent = newPrice;
          }

          // Update qty (in case Shopify merged lines)
          const qtyEl = itemEl.querySelector('.fcd-item__qty-num');
          if (qtyEl && qtyEl.textContent !== String(updatedItem.quantity)) {
            qtyEl.textContent = String(updatedItem.quantity);
          }
          const qtyDecBtn = itemEl.querySelector('.fcd-item__qty .fcd-qty-btn:first-child');
          const qtyIncBtn = itemEl.querySelector('.fcd-item__qty .fcd-qty-btn:last-child');
          if (qtyDecBtn) qtyDecBtn.dataset.qty = String(updatedItem.quantity - 1);
          if (qtyIncBtn) qtyIncBtn.dataset.qty = String(updatedItem.quantity + 1);

          // Update items-wrap signature so cart-drawer's diff stays surgical
          const itemsWrap = document.querySelector('#fcd-drawer .fcd-items-wrap');
          if (itemsWrap && cartCache) {
            const newKeys = cartCache.items
              .filter(i => i.sku !== 'SHIP-PROTECT')
              .map(i => i.key + ':' + i.quantity + ':' + i.final_line_price)
              .join(',');
            itemsWrap.dataset.keys = newKeys;
          }
        }

        // Make sure dropdown shows the new plan
        selectEl.value = String(sellingPlanId);
      }

      // Update header bits
      const totalPrice = fresh.total_price;
      const count = fresh.items.filter(i => i.sku !== 'SHIP-PROTECT')
                               .reduce((sum, i) => sum + i.quantity, 0);
      const subtotalEl = document.querySelector('.fcd-subtotal__price');
      if (subtotalEl) {
        const newSub = formatMoney(totalPrice);
        if (subtotalEl.textContent !== newSub) subtotalEl.textContent = newSub;
      }
      const checkoutEl = document.querySelector('.fcd-checkout-btn');
      if (checkoutEl) {
        const newCheckout = 'CHECKOUT • ' + formatMoney(totalPrice);
        if (checkoutEl.textContent !== newCheckout) checkoutEl.textContent = newCheckout;
      }
      document.querySelectorAll('.cart-count-bubble span:first-child, .cart-icon-bubble span, [data-cart-count]')
        .forEach(el => {
          const s = String(count);
          if (el.textContent !== s) el.textContent = s;
        });

      // Trigger protection sync (fire and forget — doesn't block the dropdown)
      if (typeof window._fcdTriggerProtectionSync === 'function') {
        window._fcdTriggerProtectionSync(fresh).catch(() => {});
      }
    } catch (err) {
      console.warn('[CustomSub] Plan change error:', err);
    } finally {
      if (selectEl.isConnected) {
        selectEl.disabled = false;
        selectEl.classList.remove('csub-select--loading');
      }
      selectsInFlight.delete(selectEl);
    }
  }

  async function upgradeToSubscription(lineKey, wrapperEl) {
    wrapperEl.style.display = 'none';

    const itemEl = wrapperEl.closest('.fcd-item');
    const handle = itemEl?.dataset?.handle ||
                   (cartCache?.items?.find(i => i.key === lineKey)?.handle);
    const variantId = itemEl?.dataset?.variantId ||
                      (cartCache?.items?.find(i => i.key === lineKey)?.variant_id);

    // Get real plans for this variant — these have the correct IDs that
    // Shopify will accept for /cart/change.js. The hardcoded SELLING_PLANS
    // are a fallback only.
    let plans = SELLING_PLANS;
    let defaultPlanId = DEFAULT_SELLING_PLAN_ID;
    let defaultPlanName = SELLING_PLANS.find(p => p.id === DEFAULT_SELLING_PLAN_ID)?.name || '';

    if (handle && variantId) {
      const planData = await getVariantSellingPlans(handle, variantId);
      if (planData.plans.length > 0) {
        plans = planData.plans;
        // Use the FIRST plan in the list as the default (typically 1 week).
        defaultPlanId = plans[0].id;
        defaultPlanName = plans[0].name;
      }
    }

    const selectorEl = buildSelector(lineKey, defaultPlanId, plans);
    wrapperEl.parentNode.insertBefore(selectorEl, wrapperEl.nextSibling);

    // Optimistically show the badge so it appears INSTANTLY
    if (itemEl && defaultPlanName) {
      ensureSubscriptionBadge(itemEl, defaultPlanName);
    }

    await changePlan(selectorEl, defaultPlanId);
  }

  async function syncExistingProperties() {
    if (!cartCache || !cartCache.items) return;
    const subItems = cartCache.items.filter(i => i.selling_plan_allocation);
    if (!subItems.length) return;

    let needsSync = false;
    for (const item of subItems) {
      const planId = String(item.selling_plan_allocation.selling_plan.id);
      const existingProp = item.properties && item.properties._selling_plan_id;
      if (String(existingProp) !== planId) { needsSync = true; break; }
    }
    if (!needsSync) return;

    for (const item of subItems) {
      const planId = String(item.selling_plan_allocation.selling_plan.id);
      const existingProp = item.properties && item.properties._selling_plan_id;
      if (String(existingProp) !== planId) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              id: item.key,
              properties: Object.assign({}, item.properties || {}, { _selling_plan_id: planId }),
            }),
          });
        } catch (err) {
          console.warn('[CustomSub] Failed to sync property for', item.title, err);
        }
      }
    }
    await fetchCart();
  }

  function buildSelector(lineKey, currentPlanId, plans) {
    // Use the per-variant real plan list if provided, otherwise fall back
    // to the hardcoded SELLING_PLANS. Real plans come from /products/{handle}.js
    // and have the correct IDs for THIS specific product (different selling
    // plan groups have different IDs).
    const planList = (Array.isArray(plans) && plans.length > 0) ? plans : SELLING_PLANS;

    const wrapper = document.createElement('div');
    wrapper.className = 'csub-wrapper';
    wrapper.setAttribute('data-line-key', lineKey);

    const label = document.createElement('span');
    label.className = 'csub-label';
    label.textContent = 'Delivery frequency';
    wrapper.appendChild(label);

    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'csub-select-wrapper';

    const select = document.createElement('select');
    select.className = 'csub-select';
    select.setAttribute('aria-label', 'Subscription delivery frequency');

    // If currentPlanId isn't in the plan list, prepend a non-selectable
    // placeholder so the dropdown doesn't appear empty. This handles the
    // edge case where the cart's plan ID isn't yet in our cache.
    const currentInList = currentPlanId &&
      planList.some(p => String(p.id) === String(currentPlanId));

    if (!currentPlanId || !currentInList) {
      const promptOpt = document.createElement('option');
      promptOpt.value = '';
      promptOpt.textContent = currentPlanId
        ? 'Loading frequencies...'
        : 'Select frequency...';
      promptOpt.disabled = true;
      promptOpt.selected = true;
      select.appendChild(promptOpt);
    }

    planList.forEach((plan) => {
      const opt = document.createElement('option');
      opt.value = plan.id;
      opt.textContent = plan.name;
      if (String(plan.id) === String(currentPlanId)) opt.selected = true;
      select.appendChild(opt);
    });

    // DIRECT change handler — attached to this specific select.
    // Simplest possible — no delegation, no global lock.
    select.addEventListener('change', function (e) {
      if (!e.target.value) return;
      changePlan(wrapper, e.target.value);
    });

    selectWrapper.appendChild(select);
    wrapper.appendChild(selectWrapper);
    return wrapper;
  }

  function buildUpgradeButton(lineKey) {
    const wrapper = document.createElement('div');
    wrapper.className = 'csub-upgrade-wrapper';
    wrapper.setAttribute('data-upgrade-key', lineKey);

    const btn = document.createElement('button');
    btn.className = 'csub-upgrade-btn';
    btn.type = 'button';

    const icon = document.createElement('span');
    icon.className = 'csub-upgrade-icon';
    icon.textContent = '🔄';

    const text = document.createElement('span');
    text.textContent = 'Subscribe & Save 5%';

    btn.appendChild(icon);
    btn.appendChild(text);
    btn.addEventListener('click', () => upgradeToSubscription(lineKey, wrapper));

    wrapper.appendChild(btn);
    return wrapper;
  }

  async function injectSelectorsIntoCustomDrawer() {
    if (isInjectingCustomDrawer) {
      console.log('[CustomSub] inject skipped — already running');
      return;
    }
    isInjectingCustomDrawer = true;

    try {
      const drawer = document.getElementById('fcd-drawer');
      if (!drawer) {
        console.log('[CustomSub] no drawer found');
        return;
      }
      if (!cartCache || !cartCache.items) {
        console.log('[CustomSub] no cart cache');
        return;
      }

      const itemEls = drawer.querySelectorAll('.fcd-item');
      console.log('[CustomSub] inject found', itemEls.length, 'items, cart has', cartCache.items.length, 'lines');
      if (!itemEls.length) return;

      for (const itemEl of itemEls) {
        const key = itemEl.dataset.key;
        if (!key) continue;

        const cartItem = cartCache.items.find(i => i.key === key);
        if (!cartItem) continue;
        if (cartItem.sku === 'SHIP-PROTECT') continue;
        if (cartItem.title && cartItem.title.toLowerCase().includes('shipping protection')) continue;

        if (itemEl.dataset.csubPending) {
          const pendingSince = parseInt(itemEl.dataset.csubPending, 10);
          if (!isNaN(pendingSince) && (Date.now() - pendingSince) < 3000) {
            console.log('[CustomSub] skipping pending item', key, 'pendingSince', pendingSince);
            continue;
          }
          // Stale: clear it
          delete itemEl.dataset.csubPending;
        }

        if (cartItem.selling_plan_allocation) {
          const planName = cartItem.selling_plan_allocation.selling_plan.name;
          ensureSubscriptionBadge(itemEl, planName);
        } else {
          removeSubscriptionBadge(itemEl);
        }

        const existingEl = itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper');
        if (existingEl) {
          const existingKey = existingEl.getAttribute('data-line-key') || existingEl.getAttribute('data-upgrade-key');
          const isExistingUpgrade = existingEl.classList.contains('csub-upgrade-wrapper');
          const isExistingSelector = existingEl.classList.contains('csub-wrapper');

          if (existingKey === key) {
            // Same line key. Determine if the existing element matches the
            // current cart state (sub vs one-time). If mismatch, replace it.

            if (cartItem.selling_plan_allocation && isExistingSelector) {
              // Subscription line with existing dropdown — sync options/value
              const sel = existingEl.querySelector('.csub-select');
              if (sel && !selectsInFlight.has(sel) && !sel.classList.contains('csub-select--loading')) {
                const realId = String(cartItem.selling_plan_allocation.selling_plan.id);
                const cachedPlans = getCachedPlansOrFallback(cartItem.handle, cartItem.variant_id);
                const isUsingRealPlans = cachedPlans !== SELLING_PLANS;
                const currentInOptions = Array.from(sel.options).some(o => o.value === realId);

                if (isUsingRealPlans) {
                  rebuildSelectOptions(sel, cachedPlans, realId);
                  if (sel.value !== realId) sel.value = realId;
                } else if (!currentInOptions) {
                  getVariantSellingPlans(cartItem.handle, cartItem.variant_id).then((planData) => {
                    if (!sel.isConnected) return;
                    if (selectsInFlight.has(sel) || sel.classList.contains('csub-select--loading')) return;
                    const realPlans = planData.plans.length > 0 ? planData.plans : SELLING_PLANS;
                    rebuildSelectOptions(sel, realPlans, realId);
                    if (sel.value !== realId) sel.value = realId;
                  }).catch(() => {});
                } else {
                  if (sel.value !== realId) sel.value = realId;
                }
              }
              continue; // dropdown is correct, don't rebuild
            }

            if (!cartItem.selling_plan_allocation && isExistingUpgrade) {
              // One-time line with existing upgrade button — leave as-is
              continue;
            }

            // Mismatch: cart state changed (sub <-> one-time) but element
            // wasn't updated. Remove and rebuild below.
            existingEl.remove();
          } else {
            // Key mismatch: remove and rebuild
            existingEl.remove();
          }
        }

        const infoEl = itemEl.querySelector('.fcd-item__info');
        if (!infoEl) continue;

        itemEl.dataset.csubPending = String(Date.now());

        try {
          if (cartItem.selling_plan_allocation) {
            const currentPlanId = cartItem.selling_plan_allocation.selling_plan.id;
            if (!itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper')) {
              const planData = await getVariantSellingPlans(cartItem.handle, cartItem.variant_id);
              if (!itemEl.isConnected) continue;
              const plans = planData.plans.length > 0 ? planData.plans : SELLING_PLANS;
              if (!itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper')) {
                infoEl.appendChild(buildSelector(cartItem.key, currentPlanId, plans));
              }
            }
          } else {
            console.log('[CustomSub] One-time item, fetching plans:', cartItem.handle, cartItem.variant_id);
            const planData = await getVariantSellingPlans(cartItem.handle, cartItem.variant_id);
            console.log('[CustomSub] One-time plan data:', planData);
            if (!itemEl.isConnected) {
              console.log('[CustomSub] itemEl disconnected after fetch');
              continue;
            }
            if (planData.hasPlans) {
              if (!itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper')) {
                console.log('[CustomSub] Appending upgrade button for', cartItem.title);
                infoEl.appendChild(buildUpgradeButton(cartItem.key));
              } else {
                console.log('[CustomSub] Wrapper already exists, skipping');
              }
            } else {
              console.log('[CustomSub] No plans available for this variant');
            }
          }
        } catch (err) {
          console.warn('[CustomSub] Inject error for line', key, err);
        } finally {
          if (itemEl.isConnected) delete itemEl.dataset.csubPending;
        }
      }
    } finally {
      isInjectingCustomDrawer = false;
    }
  }

  // Rebuild a select's <option> elements from a plan list while preserving
  // the user's current selection if possible. Used when we built a select
  // with fallback plans and now have the real plan list.
  function rebuildSelectOptions(selectEl, plans, currentPlanId) {
    if (!selectEl || !Array.isArray(plans) || plans.length === 0) return;
    // Check if the existing options already match — if so, don't rebuild
    // (avoid flicker).
    const existingIds = Array.from(selectEl.options)
      .filter(o => o.value)
      .map(o => o.value);
    const newIds = plans.map(p => String(p.id));
    if (existingIds.length === newIds.length &&
        existingIds.every((id, i) => id === newIds[i])) {
      return; // Already correct
    }

    // Clear and rebuild
    while (selectEl.firstChild) selectEl.removeChild(selectEl.firstChild);

    const currentInList = currentPlanId &&
      plans.some(p => String(p.id) === String(currentPlanId));

    if (!currentInList) {
      const promptOpt = document.createElement('option');
      promptOpt.value = '';
      promptOpt.textContent = 'Select frequency...';
      promptOpt.disabled = true;
      promptOpt.selected = true;
      selectEl.appendChild(promptOpt);
    }

    plans.forEach((plan) => {
      const opt = document.createElement('option');
      opt.value = plan.id;
      opt.textContent = plan.name;
      if (String(plan.id) === String(currentPlanId)) opt.selected = true;
      selectEl.appendChild(opt);
    });
  }

  // Tracks whether another fcdInjectSubscriptions call came in while one
  // was running. The currently-running call sees the flag and runs again
  // after it finishes, ensuring we always end up in the latest state.
  let injectRequested = false;

  window.fcdInjectSubscriptions = async function (useFreshCart) {
    if (isInjectingCustomDrawer) {
      // Already in flight. Queue another pass to run when current one
      // finishes. The currently-running pass will see this flag.
      injectRequested = true;
      return;
    }

    do {
      injectRequested = false;

      if (useFreshCart && window._fcdCart && Array.isArray(window._fcdCart.items)) {
        cartCache = window._fcdCart;
      } else {
        await fetchCart();
      }

      // Clear any stale pending markers from a previous interrupted pass.
      const drawer = document.getElementById('fcd-drawer');
      if (drawer) {
        drawer.querySelectorAll('[data-csub-pending]').forEach(el => {
          delete el.dataset.csubPending;
        });
      }

      // Prewarm plan cache so dropdowns get real per-variant plans on first render
      await prewarmPlanCache();
      await injectSelectorsIntoCustomDrawer();

      // If a re-entry came in during execution, run another pass with
      // fresh cart data so the latest state is reflected.
      useFreshCart = true;
    } while (injectRequested);
  };

  async function injectSelectors() {
    if (!cartCache || !cartCache.items) {
      const cart = await fetchCart();
      if (!cart) return;
    }

    const subEls = document.querySelectorAll('.rebuy-cart__flyout-item-subscription');

    subEls.forEach((subEl) => {
      const rebuySel = subEl.querySelector('select.rebuy-select, select[aria-label*="delivery"], select[aria-label*="Subscription"]');
      if (rebuySel) rebuySel.style.setProperty('display', 'none', 'important');
      const rebuyBtn = subEl.querySelector('button.rebuy-button, .rebuy-button');
      if (rebuyBtn) rebuyBtn.style.setProperty('display', 'none', 'important');

      const lineItemEl = subEl.closest('li') || subEl;
      const liClasses = lineItemEl.className || '';
      const planValueMatch = liClasses.match(/property-value-(\d+)/);
      const planIdFromClass = planValueMatch ? planValueMatch[1] : null;

      let matchedCartItem = null;

      if (planIdFromClass) {
        matchedCartItem = cartCache.items.find(i =>
          i.selling_plan_allocation &&
          String(i.selling_plan_allocation.selling_plan.id) === planIdFromClass
        );
      }

      if (!matchedCartItem) {
        const ariaBtn = subEl.querySelector('button[aria-label]');
        const rebuyBtnLabel = ariaBtn ? ariaBtn.getAttribute('aria-label') || '' : '';
        const titleMatch = rebuyBtnLabel.match(/^Switch (.+?) to a Subscription/i);
        const extractedTitle = titleMatch ? titleMatch[1].trim() : '';

        if (extractedTitle) {
          const shortTitle = extractedTitle.split(' ').slice(0, 4).join(' ').toLowerCase();
          matchedCartItem = cartCache.items.find(i =>
            !i.selling_plan_allocation &&
            i.title.toLowerCase().includes(shortTitle)
          );
          if (matchedCartItem) {
            if (subEl.querySelector('.csub-upgrade-wrapper, .csub-wrapper')) return;
            if (ineligibleVariants.has(String(matchedCartItem.variant_id))) return;
            subEl.appendChild(buildUpgradeButton(matchedCartItem.key));
            return;
          }
        }

        const titleEl = lineItemEl.querySelector('.rebuy-cart__flyout-item-title, [class*="item-title"]');
        const domTitle = titleEl ? (titleEl.textContent || '').trim() : '';
        if (domTitle) {
          const shortDom = domTitle.split(' ').slice(0, 4).join(' ').toLowerCase();
          matchedCartItem = cartCache.items.find(i =>
            i.selling_plan_allocation &&
            i.title.toLowerCase().includes(shortDom)
          );
          if (!matchedCartItem) {
            matchedCartItem = cartCache.items.find(i =>
              !i.selling_plan_allocation &&
              i.title.toLowerCase().includes(shortDom)
            );
            if (matchedCartItem) {
              if (subEl.querySelector('.csub-upgrade-wrapper, .csub-wrapper')) return;
              if (ineligibleVariants.has(String(matchedCartItem.variant_id))) return;
              subEl.appendChild(buildUpgradeButton(matchedCartItem.key));
              return;
            }
          }
        }
      }

      if (!matchedCartItem) return;

      const existingWrapper = subEl.querySelector('.csub-wrapper');
      if (existingWrapper) {
        const lineKey = existingWrapper.getAttribute('data-line-key');
        if (lineKey === matchedCartItem.key) {
          const select = existingWrapper.querySelector('.csub-select');
          const currentId = String(matchedCartItem.selling_plan_allocation.selling_plan.id);
          if (select && !selectsInFlight.has(select) && !select.classList.contains('csub-select--loading') && select.value !== currentId) {
            select.value = currentId;
          }
          return;
        }
        existingWrapper.remove();
      }

      const existingUpgrade = subEl.querySelector('.csub-upgrade-wrapper');
      if (existingUpgrade) existingUpgrade.remove();

      const currentPlanId = matchedCartItem.selling_plan_allocation.selling_plan.id;
      const cachedPlans = getCachedPlansOrFallback(matchedCartItem.handle, matchedCartItem.variant_id);
      subEl.appendChild(buildSelector(matchedCartItem.key, currentPlanId, cachedPlans));
    });
  }

  function injectStyles() {
    if (document.getElementById('csub-styles')) return;

    const style = document.createElement('style');
    style.id = 'csub-styles';
    style.textContent = `
      .csub-wrapper { display:flex; flex-direction:column; gap:5px; margin-top:10px; width:100%; }
      .csub-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; color:#1f5c35; }
      .csub-select-wrapper { position:relative; width:100%; }
      .csub-select-wrapper::after { content:''; position:absolute; right:11px; top:50%; transform:translateY(-50%); width:0; height:0; border-left:4px solid transparent; border-right:4px solid transparent; border-top:5px solid #1f5c35; pointer-events:none; }
      .csub-select { width:100%; appearance:none; -webkit-appearance:none; -moz-appearance:none; background-color:#eef4f0; background-image:none!important; border:1.5px solid #c2d9c9; border-radius:6px; padding:8px 30px 8px 11px; font-size:13px; font-weight:500; color:#1a3d25; cursor:pointer; font-family:inherit; line-height:1.4; transition:border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease; }
      .csub-select::-ms-expand { display:none; }
      .csub-select:hover { border-color:#1f5c35; background-color:#e3efe6; }
      .csub-select:focus { outline:none; border-color:#1f5c35; background-color:#fff; box-shadow:0 0 0 3px rgba(31,92,53,0.15); }
      .csub-select--loading { opacity:0.55; cursor:wait; }
      .rebuy-cart__flyout-item-subscription .rebuy-button.outline,
      .rebuy-cart__flyout-item-subscription button.rebuy-button { display:none!important; }
      .csub-upgrade-wrapper { margin-top:8px; width:100%; }
      .csub-upgrade-btn { display:flex; align-items:center; gap:6px; width:100%; background-color:#eef4f0; border:1.5px solid #1f5c35; border-radius:6px; padding:8px 12px; font-size:12px; font-weight:600; color:#1f5c35; cursor:pointer; font-family:inherit; text-align:left; transition:background-color 0.18s ease, color 0.18s ease; }
      .csub-upgrade-btn:hover { background-color:#1f5c35; color:#fff; }
      .csub-upgrade-btn:disabled { opacity:0.55; cursor:wait; }
      .csub-upgrade-icon { font-size:14px; line-height:1; }
    `;

    document.head.appendChild(style);
  }

  function scheduleInject(delay = 50) {
    if (injectScheduled) return;
    injectScheduled = true;
    setTimeout(async () => {
      if (window._fcdCart && Array.isArray(window._fcdCart.items)) cartCache = window._fcdCart;
      else await fetchCart();
      await prewarmPlanCache();
      await injectSelectors();
      await injectSelectorsIntoCustomDrawer();
      injectScheduled = false;
    }, delay);
  }

  function startObserver() {
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) =>
        [...m.addedNodes].some(
          (n) =>
            n.nodeType === 1 &&
            (n.matches?.('.rebuy-cart__flyout-item-subscription') ||
              n.querySelector?.('.rebuy-cart__flyout-item-subscription') ||
              n.matches?.('[data-rebuy-cart-anchor]') ||
              n.querySelector?.('[data-rebuy-cart-anchor]') ||
              n.matches?.('.rebuy-cart__flyout-item') ||
              n.querySelector?.('.rebuy-cart__flyout-item') ||
              n.matches?.('.fcd-items-wrap') ||
              n.querySelector?.('.fcd-items-wrap') ||
              n.matches?.('.fcd-items') ||
              n.querySelector?.('.fcd-items') ||
              n.matches?.('.fcd-item') ||
              n.querySelector?.('.fcd-item'))
        )
      );
      if (relevant) scheduleInject(50);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function listenForUpdates() {
    document.addEventListener('rebuy:cart.change', () => scheduleInject(100));
    document.addEventListener('rebuy:smartcart.ready', () => scheduleInject(100));
  }

  async function prewarmPlanCache() {
    if (!cartCache || !cartCache.items) return;
    const subItems = cartCache.items.filter(i =>
      i.sku !== 'SHIP-PROTECT' && i.handle && i.variant_id
    );
    if (!subItems.length) return;
    // Fetch in parallel to populate productPlanCache before any UI renders.
    await Promise.all(subItems.map(item =>
      getVariantSellingPlans(item.handle, item.variant_id).catch(() => null)
    ));
  }

  async function init() {
    injectStyles();
    startObserver();
    listenForUpdates();
    await fetchCart();
    await syncExistingProperties();
    await prewarmPlanCache();
    await injectSelectors();
    await injectSelectorsIntoCustomDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();